import React from "react";
import {
  Box,
  Text,
  Flex,
  Badge,
  HStack,
  VStack,
  Icon,
  Spinner as ChakraMiniSpinner,
} from "@chakra-ui/react";
import { FaAward } from "react-icons/fa";
import { IoAlertCircleOutline } from "react-icons/io5";
import { MdOutlinePhoneIphone } from "react-icons/md";
import { handleDefaultRanking } from "@helpers/defaultValues";
import ButtonDropdown from "@components/Sales/TableMyOportunity/ButtonDropdown";
import { theme } from "src/styles/theme";
import CustomTooltip from "@components/Sales/TableMyOportunity/CustomTooltip";
import salesService from "@services/Sales/SalesService";
import type {
  OrdenarPor,
  TipoOrdenacao,
} from "@components/Sales/Inputs/InputSortBy";

// ─── Types ────────────────────────────────────────────────────────────────────

type TableRow = {
  id: string;
  fase: string;
  status: string;
  nomeDaConta: string;
  nomeDaUnidade: string;
  oportunidadeId: string;
  opportunityIdSalesforce: string;
  idEmpreendimentoSales: string;
  imobiliaria: string;
  imobiliariaId: string;
  ranking: string | null;
  criadorDaOportunidade: string;
  dataUltimaModificacao: string;
  empreendimentoDeInteresse: string;
  diasSemAtualizacao: number;
  origemDigital?: boolean;
  PermitidoQueda?: boolean;
  telefonePrincipal?: string;
  tipoNotificacao?: string;
  nomeDaCotacao?: string;
  idCotacaoSalesforce?: string;
  statusAnaliseDeCredito?: string[];
  statusCotacao?: string[];
};

interface IKanbanFilters {
  idCorretor: string;
  search: string;
  oportunityCreator: string | string[];
  dateStart: string;
  dateEnd: string;
  // Mesmos parâmetros usados no modo tabela para filtros especiais
  // (Oportunidades vencidas, OP's Cliente Ouro, Contrato) e de ranking
  // (Bronze, Prata, Ouro, Diamante, Aço, Não elegível).
  filtro?: number;
  rank?: number | null;
  // Ordenação — sempre resolvida no backend, igual ao modo tabela.
  ordenarPor?: OrdenarPor;
  tipoOrdenacao?: TipoOrdenacao;
}

interface IKanbanProps extends Omit<IKanbanFilters, "filtro" | "rank"> {
  // Fases selecionadas no filtro (já em minúsculo). Vazio = nenhuma fase
  // selecionada -> cada coluna busca usando suas próprias fases padrão.
  selectedFases: string[];
  // Mesma lógica de filtro especial / ranking do modo tabela.
  filtro?: number;
  rank?: number | null;
  // Incrementar esse valor força uma nova busca em todas as colunas
  // (ex: clique no botão "Atualizar").
  refreshKey?: number;
  onClickCard: (row: TableRow) => void;
  renderDropdown: (row: TableRow) => React.ReactNode;
}

// ─── Columns definition ───────────────────────────────────────────────────────

const KANBAN_COLUMNS: { title: string; fases: string[] }[] = [
  {
    title: "Aguardando atendimento",
    fases: ["aguardando atendimento corretor", "aguardando atendimento"],
  },
  {
    title: "Em tratativa",
    fases: [
      "em atendimento",
      "em tratativa",
      "visita agendada",
      "visita realizada",
    ],
  },
  {
    title: "Análise de crédito",
    fases: [
      "análise de crédito",
      "analise de credito",
      "em análise de crédito",
      "análise de crédito realizada",
      "enviado para análise",
      "enviado para analise",
      "análise aprovada",
      "analise aprovada",
      "análise reprovada",
      "analise reprovada",
      "rascunho",
    ],
  },
  {
    title: "Proposta",
    fases: ["proposta", "proposta aprovada", "proposta reprovada"],
  },
  {
    title: "Em processamento",
    fases: [
      "em processamento",
      "contrato com pendência comercial",
      "contrato com pendencia comercial",
      "contrato comunicado",
      "contrato gerado",
      "análise safi",
      "analise safi",
      "aprovado safi",
      "rejeitado safi",
      "enviado aprovação comissões",
      "enviado aprovacao comissoes",
      "aprovado comissões",
      "aprovado comissoes",
      "reprovado comissões",
      "reprovado comissoes",
      "enviado aprovação pró soluto",
      "enviado aprovacao pro soluto",
      "aprovado pró soluto",
      "aprovado pro soluto",
      "rejeitado pró soluto",
      "rejeitado pro soluto",
    ],
  },
  {
    title: "Finalizados",
    fases: [
      "fechado e ganho",
      "fechado e perdido",
      "repassado",
      "cancelado",
      "cancelada",
      "distratado",
    ],
  },
];

// Dado o valor de "fase" de um item vindo da API, descobre a qual coluna do
// Kanban ele pertence. Usado no modo de busca compartilhada (quando rank ou
// filtro estão ativos e o backend não filtra por fase — ver useSharedKanbanFeed
// mais abaixo), pra distribuir os itens retornados nas colunas certas.
const getColumnIndexForFase = (fase?: string): number => {
  const faseLower = fase?.toLowerCase()?.trim() ?? "";
  if (!faseLower) return -1;

  // Primeiro tenta um match exato com alguma das fases da coluna.
  const exactIndex = KANBAN_COLUMNS.findIndex((col) =>
    col.fases.some((f) => f === faseLower),
  );
  if (exactIndex !== -1) return exactIndex;

  // Fallback: match parcial (contains) em ambas as direções, pra cobrir
  // pequenas variações de texto que não batem 100% com a lista fixa.
  return KANBAN_COLUMNS.findIndex((col) =>
    col.fases.some((f) => faseLower.includes(f) || f.includes(faseLower)),
  );
};

const BADGE_COLORS = {
  gray: { bg: "#F5F7FA", color: "#59667A", border: "#D1D9E0" },
  grayPurple: { bg: "#F7F0FD", color: "#6D33A0", border: "#D1D9E0" },
  orange: { bg: "#FDF3DF", color: "#946300", border: "#FFCC80" },
  pink: { bg: "#FEEDF5", color: "#A72568", border: "#F9A8D4" },
  blue: { bg: "#F0F4FD", color: "#3353A0", border: "#93C5FD" },
  green: { bg: "#EEFCF1", color: "#1A8431", border: "#86EFAC" },
  red: { bg: "#FAEEEE", color: "#CC313E", border: "#FECACA" },
  purple: { bg: "#F5F3FF", color: "#6D28D9", border: "#C4B5FD" },
};

const getStatusBadgeColors = (fase: string) => {
  const s = fase?.toLowerCase() || "";

  if (
    s.includes("aguardando") ||
    s.includes("distratado") ||
    s.includes("repassado")
  )
    return BADGE_COLORS.gray;

  if (s.includes("rascunho") || s.includes("contrato gerado"))
    return BADGE_COLORS.grayPurple;

  if (
    s === "em atendimento" ||
    s === "em tratativa" ||
    s.includes("enviado para análise") ||
    s.includes("enviado para analise") ||
    s === "proposta" ||
    s.includes("enviado aprovação comissões") ||
    s.includes("enviado aprovacao comissoes") ||
    s.includes("enviado aprovação pró soluto") ||
    s.includes("enviado aprovacao pro soluto")
  )
    return BADGE_COLORS.orange;

  if (
    s.includes("visita agendada") ||
    s.includes("proposta reprovada") ||
    s.includes("pendência") ||
    s.includes("pendencia") ||
    s.includes("contrato comunicado") ||
    s.includes("rejeitado safi") ||
    s.includes("reprovado comissões") ||
    s.includes("reprovado comissoes") ||
    s.includes("rejeitado pró soluto") ||
    s.includes("rejeitado pro soluto")
  )
    return BADGE_COLORS.pink;

  if (
    s.includes("visita realizada") ||
    s.includes("análise safi") ||
    s.includes("analise safi") ||
    s.includes("aprovado safi") ||
    s.includes("aprovado pró soluto") ||
    s.includes("aprovado pro soluto")
  )
    return BADGE_COLORS.blue;

  if (
    s.includes("análise aprovada") ||
    s.includes("analise aprovada") ||
    s.includes("proposta aprovada") ||
    s.includes("aprovado comissões") ||
    s.includes("aprovado comissoes") ||
    s.includes("fechado e ganho")
  )
    return BADGE_COLORS.green;

  if (
    s.includes("análise reprovada") ||
    s.includes("analise reprovada") ||
    s.includes("fechado e perdido") ||
    s.includes("cancelad")
  )
    return BADGE_COLORS.red;

  return BADGE_COLORS.blue;
};

const handleAdjustFase = (value: string) => {
  if (value?.toLowerCase() === "aguardando atendimento corretor")
    return "Aguardando atendimento corretor";
  return value;
};

const formatSla = (dias: number) => {
  if (!dias || dias === 0) return null;
  if (dias === 1) return "1 dia sem atualização";
  return `${dias} dias sem atualização`;
};

// Normaliza status (pode vir como array, string ou undefined)
const getStatusList = (status?: string[] | string): string[] => {
  if (!status) return [];
  return Array.isArray(status) ? status.filter(Boolean) : [status];
};

// ─── Card ─────────────────────────────────────────────────────────────────────

const KanbanCard = React.memo(function KanbanCard({
  row,
  onClickCard,
  renderDropdown,
}: {
  row: TableRow;
  onClickCard: (row: TableRow) => void;
  renderDropdown: (row: TableRow) => React.ReactNode;
}) {
  const slaText = formatSla(row.diasSemAtualizacao);

  const fase = row.fase?.toLowerCase() ?? "";

  const isCreditStage = KANBAN_COLUMNS[2].fases.some((f) => fase.includes(f));

  const isProposalStage = KANBAN_COLUMNS.slice(3).some((column) =>
    column.fases.some((f) => fase.includes(f)),
  );

  const creditStatusList =
    isCreditStage || isProposalStage
      ? getStatusList(row.statusAnaliseDeCredito)
      : [];

  const quoteStatusList = isProposalStage
    ? getStatusList(row.statusCotacao)
    : [];

  const hasCreditStatus = creditStatusList.length > 0;
  const hasQuoteStatus = quoteStatusList.length > 0;

  // Quando existem status de análise de crédito, usamos o primeiro como
  // referência para a cor/label principal do badge.
  const badgeValue = row.fase;

  const badgeColors = getStatusBadgeColors(badgeValue);

  const statusLabel = handleAdjustFase(badgeValue);

  const extraCount =
    (hasCreditStatus ? creditStatusList.length : 0) +
    (hasQuoteStatus ? quoteStatusList.length : 0);

  // const badgeDisplayLabel =
  //   extraCount > 0 ? `${statusLabel} (${extraCount})` : statusLabel;

  const badgeDisplayLabel = statusLabel;

  const tooltipLabel = (
    <Box minW="220px">
      {hasCreditStatus && (
        <>
          <Text
            fontSize="12px"
            fontWeight="700"
            color={theme.text}
            borderBottom="1px solid"
            borderColor="#E2E8F0"
            pb="6px"
            mb="6px"
          >
            Análise de crédito
          </Text>

          <VStack spacing="4px" align="start" mb="8px">
            {creditStatusList.map((status, idx) => (
              <Text key={`credito-${idx}`} fontSize="12px">
                {status}
              </Text>
            ))}
          </VStack>
        </>
      )}

      {hasQuoteStatus && (
        <>
          <Text
            fontSize="12px"
            fontWeight="700"
            color={theme.text}
            borderBottom="1px solid"
            borderColor="#E2E8F0"
            pb="6px"
            mb="6px"
          >
            Cotações
          </Text>

          <VStack spacing="4px" align="start">
            {quoteStatusList.map((status, idx) => (
              <Text key={`cotacao-${idx}`} fontSize="12px">
                {status}
              </Text>
            ))}
          </VStack>
        </>
      )}
    </Box>
  );

  const StatusBadge = (
    <Badge
      bg={badgeColors.bg}
      color={badgeColors.color}
      border="1px solid"
      borderColor={badgeColors.border}
      borderRadius="full"
      px="10px"
      py="3px"
      fontSize="11px"
      fontWeight="500"
      display="inline-block"
      textTransform="none"
      letterSpacing="0"
      w="fit-content"
    >
      {badgeDisplayLabel}
    </Badge>
  );

  return (
    <Box
      bg="white"
      borderRadius="8px"
      p="10px"
      cursor="pointer"
      transition="box-shadow 0.15s ease, border-color 0.15s ease"
      _hover={{ boxShadow: "0 2px 8px rgba(0,0,0,0.10)" }}
      onClick={() => onClickCard(row)}
      borderWidth={row.origemDigital ? 2 : undefined}
      borderLeftColor={row.origemDigital ? theme.error50 : undefined}
    >
      {/* Nome + menu */}
      <Flex justify="space-between" align="flex-start" mb="2px">
        <HStack spacing="6px" flex={1} align="center" minW={0}>
          <Text
            fontSize="13px"
            fontWeight="600"
            color="#1A202C"
            noOfLines={2}
            lineHeight="1.35"
            mr="4px"
            w={"fit-content"}
          >
            {row.nomeDaConta}
          </Text>
          {row.ranking !== null && row.ranking !== undefined && (
            <Icon
              as={FaAward}
              color={handleDefaultRanking(row.ranking)}
              boxSize="13px"
              flexShrink={0}
            />
          )}
        </HStack>

        <Box
          flexShrink={0}
          mt={"-10px"}
          ml="4px"
          w="30px"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <ButtonDropdown popoverContent={renderDropdown(row)} />
        </Box>
      </Flex>

      {/* ID */}
      <Text fontSize="11px" color={theme.text50} mb="8px" lineHeight="1.2">
        {row.oportunidadeId}
      </Text>

      {/* Badge de fase / análise de crédito + pill digital */}
      <Flex align="center" gap="6px" mb="8px" flexWrap="wrap">
        {hasCreditStatus || hasQuoteStatus ? (
          <CustomTooltip content={tooltipLabel}>{StatusBadge}</CustomTooltip>
        ) : (
          StatusBadge
        )}

        {/* Pill de origem digital */}
        {/* {row.origemDigital && (
          <Badge
            bg="#EFF6FF"
            color="#2563EB"
            border="1px solid"
            borderColor="#BFDBFE"
            borderRadius="full"
            px="8px"
            py="3px"
            fontSize="11px"
            fontWeight="500"
            display="inline-flex"
            alignItems="center"
            gap="3px"
            textTransform="none"
            letterSpacing="0"
            w="fit-content"
          >
            <Icon as={MdOutlinePhoneIphone} boxSize="11px" />
            Digital
          </Badge>
        )} */}
      </Flex>

      {/* Corretor / empreendimento */}
      <Text fontSize="11px" color="#94A3B8" noOfLines={1} lineHeight="1.3">
        {[row.empreendimentoDeInteresse, row.criadorDaOportunidade]
          .filter(Boolean)
          .join(" • ")}
      </Text>

      {/* SLA */}
      {slaText && (
        <HStack spacing="4px" mt="8px">
          <Icon as={IoAlertCircleOutline} color="#EF4444" boxSize="13px" />
          <Text fontSize="11px" color="#EF4444" fontWeight="500">
            {slaText}
          </Text>
        </HStack>
      )}
    </Box>
  );
});

// ─── Busca por coluna ───────────────────────────────────────────────────────

const ITEMS_PER_PAGE_COLUMN = 20;
// Usado no modo de busca compartilhada (rank/filtro ativos): uma única
// requisição alimenta todas as colunas, então usamos uma página maior.
const ITEMS_PER_PAGE_SHARED = 100;
const DEBOUNCE_MS = 600;
const DEBOUNCE_FILTERS_MS = 800;
const DEBOUNCE_CLEAR_FILTERS_MS = 100;

// Debounce simples — evita disparar 1 requisição por coluna a cada tecla
// digitada / a cada toggle de checkbox nos filtros.
function useDebouncedValue<T>(value: T, delay = DEBOUNCE_MS): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// Mesma regra de busca da tabela: só propaga o termo quando tiver 3+
// caracteres ou estiver vazio (evita reqs com termos curtos demais).
function useDebouncedSearch(value: string, delay = DEBOUNCE_MS): string {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    if (value.length !== 0 && value.length < 3) return;
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// ─── Busca compartilhada (modo rank / filtro especial) ───────────────────────
//
// Quando o filtro ativo é de ranking (Bronze, Prata, Ouro, Diamante, Aço, Não
// elegível) ou de categoria especial (Oportunidades vencidas, OP's Cliente
// Ouro, Contrato), o backend ignora a lista de fases enviada e retorna todos
// os itens que batem com esse filtro — não importa a fase. Se cada coluna
// fizesse sua própria requisição nesse cenário, todas receberiam de volta o
// mesmo conjunto completo de itens (foi exatamente o bug relatado: tudo
// aparecendo em todas as colunas).
//
// A solução é fazer UMA única busca aqui no componente pai (sem restringir
// por fase) e distribuir os itens retornados nas colunas corretas no
// cliente, usando o campo `fase` de cada item (via getColumnIndexForFase).
function useSharedKanbanFeed(
  filters: IKanbanFilters,
  selectedFases: string[],
  active: boolean,
  refreshKey?: number,
) {
  const [items, setItems] = React.useState<TableRow[]>([]);
  const [page, setPage] = React.useState(1);
  const [totalPaginas, setTotalPaginas] = React.useState(0);
  const [totalRegistros, setTotalRegistros] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);

  const selectedFasesKey = selectedFases.join("|");

  const fetchPage = React.useCallback(
    async (pageToFetch: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const response = await salesService.getOportunity({
          idCorretorSalesforce: filters.idCorretor,
          DataInicio: filters.dateStart,
          DataFim: filters.dateEnd,
          filtro: filters.filtro,
          rank: filters.rank,
          filtroCriadorOportunidade: filters.oportunityCreator,
          fase: selectedFases,
          clienteOuIdDaOportunidade: filters.search,
          itensPorPagina: ITEMS_PER_PAGE_SHARED,
          Pagina: pageToFetch,
          ordenarPor: filters.ordenarPor,
          tipoOrdenacao: filters.tipoOrdenacao,
        });

        if (response.status === 401 || response.status === 400) {
          if (!append) setItems([]);
          setTotalPaginas(0);
          setTotalRegistros(0);
          return;
        }

        const newItems: TableRow[] = Array.isArray(response?.itens)
          ? response.itens
          : [];

        setItems((prev) => {
          const base = append ? prev : [];
          const map = new Map<string, TableRow>();
          [...base, ...newItems].forEach((row) => {
            if (!map.has(row.oportunidadeId)) map.set(row.oportunidadeId, row);
          });
          return Array.from(map.values());
        });
        setPage(response?.paginaAtual ?? pageToFetch);
        setTotalPaginas(response?.totalPaginas ?? 0);
        setTotalRegistros(response?.totalRegistros ?? 0);
      } catch (error) {
        console.log("errorHandleGetKanbanSharedFeed", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters.idCorretor,
      filters.dateStart,
      filters.dateEnd,
      filters.search,
      filters.oportunityCreator,
      filters.filtro,
      filters.rank,
      filters.ordenarPor,
      filters.tipoOrdenacao,
      selectedFasesKey,
    ],
  );

  React.useEffect(() => {
    if (!active) {
      setItems([]);
      setTotalPaginas(0);
      setTotalRegistros(0);
      setPage(1);
      return;
    }
    setPage(1);
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, fetchPage, refreshKey]);

  const handleLoadMore = React.useCallback(() => {
    if (loadingMore) return;
    fetchPage(page + 1, true);
  }, [fetchPage, loadingMore, page]);

  const hasMore = page < totalPaginas;

  return {
    items,
    loading,
    loadingMore,
    hasMore,
    totalRegistros,
    handleLoadMore,
  };
}

// ─── Column ───────────────────────────────────────────────────────────────────

function KanbanColumn({
  title,
  columnFases,
  filters,
  selectedFases,
  refreshKey,
  onClickCard,
  renderDropdown,
  sharedMode = false,
  sharedItems,
  sharedLoading,
  sharedLoadingMore,
  sharedHasMore,
  onSharedLoadMore,
}: {
  title: string;
  columnFases: string[];
  filters: IKanbanFilters;
  selectedFases: string[];
  refreshKey?: number;
  onClickCard: (row: TableRow) => void;
  renderDropdown: (row: TableRow) => React.ReactNode;
  // Modo "compartilhado": ativo quando rank/filtro estão setados. Nesse
  // modo a coluna não faz sua própria requisição — apenas exibe os itens já
  // buscados e distribuídos pelo componente pai (ver useSharedKanbanFeed).
  sharedMode?: boolean;
  sharedItems?: TableRow[];
  sharedLoading?: boolean;
  sharedLoadingMore?: boolean;
  sharedHasMore?: boolean;
  onSharedLoadMore?: () => void;
}) {
  // ── Estado de dados/paginação por coluna (usado apenas fora do sharedMode) ──
  const [items, setItems] = React.useState<TableRow[]>([]);
  const [page, setPage] = React.useState(1);
  const [totalPaginas, setTotalPaginas] = React.useState(0);
  const [totalRegistros, setTotalRegistros] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);

  // Fases desta coluna que serão buscadas. Por padrão (nenhuma fase
  // selecionada no filtro) usamos todas as fases da coluna, já trazendo os
  // itens desde a primeira renderização. O filtro de fase apenas restringe
  // esse conjunto quando o usuário seleciona algo.
  const normalize = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const fasesToQuery = React.useMemo(() => {
    if (!selectedFases || selectedFases.length === 0) return columnFases;

    if (selectedFases.includes("todas")) return columnFases;

    return columnFases.filter((fase) =>
      selectedFases.some((sel) => normalize(sel) === normalize(fase)),
    );
  }, [columnFases, selectedFases]);

  const fasesForRequest = React.useMemo(() => {
    if (fasesToQuery.length > 0) return fasesToQuery;
    return [];
  }, [fasesToQuery]);

  // Identifica a chamada mais recente de fetchPage desta coluna. Serve pra
  // ignorar respostas que cheguem fora de ordem — por exemplo, a requisição
  // "default" (sem filtro) disparada no mount, que pode responder DEPOIS que
  // o usuário já aplicou um filtro e o estado já foi limpo/atualizado. Sem
  // isso, essa resposta atrasada sobrescrevia o estado novo com dados velhos.
  const requestIdRef = React.useRef(0);

  const fetchPage = React.useCallback(
    async (pageToFetch: number, append: boolean) => {
      if (sharedMode) return;

      const requestId = ++requestIdRef.current;

      if (fasesForRequest.length === 0) {
        setItems([]);
        setTotalPaginas(0);
        setTotalRegistros(0);
        return;
      }

      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const response = await salesService.getOportunity({
          idCorretorSalesforce: filters.idCorretor,
          DataInicio: filters.dateStart,
          DataFim: filters.dateEnd,
          filtro: filters.filtro,
          rank: filters.rank,
          filtroCriadorOportunidade: filters.oportunityCreator,
          fase: fasesForRequest,
          clienteOuIdDaOportunidade: filters.search,
          itensPorPagina: ITEMS_PER_PAGE_COLUMN,
          Pagina: pageToFetch,
          ordenarPor: filters.ordenarPor,
          tipoOrdenacao: filters.tipoOrdenacao,
        });

        // Resposta obsoleta: já existe uma chamada mais nova em andamento
        // (ou já concluída) pra esta coluna — descarta este resultado.
        if (requestId !== requestIdRef.current) return;

        if (response.status === 401 || response.status === 400) {
          if (!append) setItems([]);
          setTotalPaginas(0);
          setTotalRegistros(0);
          return;
        }

        const newItems: TableRow[] = Array.isArray(response?.itens)
          ? response.itens
          : [];

        setItems((prev) => {
          const base = append ? prev : [];
          const map = new Map<string, TableRow>();
          [...base, ...newItems].forEach((row) => {
            if (!map.has(row.oportunidadeId)) map.set(row.oportunidadeId, row);
          });
          return Array.from(map.values());
        });
        setPage(response?.paginaAtual ?? pageToFetch);
        setTotalPaginas(response?.totalPaginas ?? 0);
        setTotalRegistros(response?.totalRegistros ?? 0);
      } catch (error) {
        console.log("errorHandleGetKanbanColumn", error);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [
      sharedMode,
      fasesForRequest,
      filters.idCorretor,
      filters.dateStart,
      filters.dateEnd,
      filters.search,
      filters.oportunityCreator,
      filters.filtro,
      filters.rank,
      filters.ordenarPor,
      filters.tipoOrdenacao,
    ],
  );

  // Sempre que os filtros ou as fases desta coluna mudarem, recomeça do zero.
  // Não faz nada em sharedMode — a busca é feita pelo componente pai.
  React.useEffect(() => {
    if (sharedMode) return;
    setPage(1);
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sharedMode,
    fasesForRequest.join("|"),
    filters.idCorretor,
    filters.dateStart,
    filters.dateEnd,
    filters.search,
    filters.filtro,
    filters.rank,
    filters.ordenarPor,
    filters.tipoOrdenacao,
    JSON.stringify(filters.oportunityCreator),
    refreshKey,
  ]);

  const handleLoadMore = () => {
    if (loadingMore) return;
    fetchPage(page + 1, true);
  };

  // ── Escolhe a fonte de dados: compartilhada (rank/filtro) ou própria ──
  const displayItems = sharedMode ? (sharedItems ?? []) : items;
  const isLoading = sharedMode ? !!sharedLoading : loading;
  const isLoadingMore = sharedMode ? !!sharedLoadingMore : loadingMore;
  const hasMore = sharedMode ? !!sharedHasMore : page < totalPaginas;
  const displayTotal = sharedMode ? displayItems.length : totalRegistros;
  const handleLoadMoreFinal = sharedMode
    ? (onSharedLoadMore ?? (() => {}))
    : handleLoadMore;

  return (
    <Box
      minW="260px"
      maxW="275px"
      flex="0 0 220px"
      display="flex"
      flexDirection="column"
      maxH="100%"
      bg="#F7F7F8"
      px={4}
      py={2}
      borderRadius="2xl"
    >
      {/* Header com contador */}
      <Flex align="center" justify="space-between" mb="10px" px="2px">
        <HStack spacing="6px" minW={0} flex={1}>
          <Text
            fontSize="13px"
            fontWeight="semibold"
            color={theme.text}
            noOfLines={1}
          >
            {title}
          </Text>
          {/* Contador de cards */}
          <Badge
            bg={displayTotal > 0 ? theme.primary + "18" : "#E2E8F0"}
            color={displayTotal > 0 ? theme.primary : "#94A3B8"}
            borderRadius="full"
            px="7px"
            py="1px"
            fontSize="11px"
            fontWeight="600"
            textTransform="none"
            flexShrink={0}
            w={"fit-content"}
          >
            {displayTotal}
          </Badge>
        </HStack>
      </Flex>

      {/* Cards */}
      <VStack
        spacing="8px"
        flex="1"
        overflowY="auto"
        align="stretch"
        pb="8px"
        sx={{
          "&::-webkit-scrollbar": { width: "3px" },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-track": { background: "transparent" },
        }}
      >
        {isLoading ? (
          <Flex align="center" justify="center" h="60px">
            <ChakraMiniSpinner size="sm" color={theme.primary} />
          </Flex>
        ) : displayItems.length === 0 ? (
          <Flex
            align="center"
            justify="center"
            h="60px"
            borderRadius="8px"
            border="1px dashed"
            borderColor={theme.disable}
          >
            <Text fontSize="11px" color={theme.text50} w="fit-content">
              Sem oportunidades
            </Text>
          </Flex>
        ) : (
          <>
            {displayItems.map((row) => (
              <KanbanCard
                key={row.oportunidadeId}
                row={row}
                onClickCard={onClickCard}
                renderDropdown={renderDropdown}
              />
            ))}

            {hasMore && (
              <Box
                as="button"
                onClick={handleLoadMoreFinal}
                w="100%"
                py="8px"
                borderRadius="8px"
                border="1px dashed"
                borderColor={theme.primary}
                color={theme.primary}
                fontSize="12px"
                fontWeight="600"
                textAlign="center"
                _hover={{ bg: theme.primary + "10" }}
                opacity={isLoadingMore ? 0.6 : 1}
                cursor={isLoadingMore ? "not-allowed" : "pointer"}
              >
                {isLoadingMore ? "Carregando..." : "Ver mais"}
              </Box>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

function KanbanMyOportunity({
  idCorretor,
  search,
  oportunityCreator,
  dateStart,
  dateEnd,
  selectedFases,
  filtro,
  rank,
  ordenarPor,
  tipoOrdenacao,
  refreshKey,
  onClickCard,
  renderDropdown,
}: IKanbanProps) {
  // Debounce em campos que mudam a cada tecla digitada / a cada toggle,
  // pra não disparar uma requisição por coluna a cada interação.

  const debouncedRefreshKey = useDebouncedValue(
    refreshKey,
    DEBOUNCE_FILTERS_MS,
  );
  const debouncedSearch = useDebouncedSearch(search, DEBOUNCE_FILTERS_MS);
  const debouncedDateStart = useDebouncedValue(dateStart, DEBOUNCE_FILTERS_MS);
  const debouncedDateEnd = useDebouncedValue(dateEnd, DEBOUNCE_FILTERS_MS);
  const debouncedOportunityCreator = useDebouncedValue(
    oportunityCreator,
    DEBOUNCE_FILTERS_MS,
  );
  const debouncedSelectedFases = useDebouncedValue(
    selectedFases,
    DEBOUNCE_FILTERS_MS,
  );
  const debouncedFiltro = useDebouncedValue(filtro, DEBOUNCE_FILTERS_MS);
  const debouncedRank = useDebouncedValue(rank, DEBOUNCE_FILTERS_MS);
  const debouncedOrdenarPor = useDebouncedValue(
    ordenarPor,
    DEBOUNCE_FILTERS_MS,
  );
  const debouncedTipoOrdenacao = useDebouncedValue(
    tipoOrdenacao,
    DEBOUNCE_FILTERS_MS,
  );

  const filters = React.useMemo<IKanbanFilters>(
    () => ({
      idCorretor,
      search: debouncedSearch,
      oportunityCreator: debouncedOportunityCreator,
      dateStart: debouncedDateStart,
      dateEnd: debouncedDateEnd,
      filtro: debouncedFiltro,
      rank: debouncedRank,
      ordenarPor: debouncedOrdenarPor,
      tipoOrdenacao: debouncedTipoOrdenacao,
    }),
    [
      idCorretor,
      debouncedSearch,
      debouncedOportunityCreator,
      debouncedDateStart,
      debouncedDateEnd,
      debouncedFiltro,
      debouncedRank,
      debouncedOrdenarPor,
      debouncedTipoOrdenacao,
    ],
  );

  // Rank (Bronze, Prata, Ouro, Diamante, Aço, Não elegível) e filtro
  // especial (Oportunidades vencidas, OP's Cliente Ouro, Contrato) fazem o
  // backend ignorar a fase enviada. Nesse caso usamos busca compartilhada
  // (uma request só) em vez de uma por coluna.
  const hasGlobalFilter =
    (filters.filtro !== undefined && filters.filtro !== null) ||
    (filters.rank !== undefined && filters.rank !== null);

  const sharedFeed = useSharedKanbanFeed(
    filters,
    debouncedSelectedFases,
    hasGlobalFilter,
    debouncedRefreshKey,
  );

  // Distribui os itens da busca compartilhada nas colunas corretas,
  // usando o campo `fase` de cada item.
  const itemsByColumn = React.useMemo(() => {
    const map = new Map<string, TableRow[]>();
    KANBAN_COLUMNS.forEach((col) => map.set(col.title, []));

    if (!hasGlobalFilter) return map;

    sharedFeed.items.forEach((item) => {
      const idx = getColumnIndexForFase(item.fase);
      if (idx === -1) return;
      const col = KANBAN_COLUMNS[idx];
      map.get(col.title)?.push(item);
    });

    return map;
  }, [hasGlobalFilter, sharedFeed.items]);

  return (
    <Box
      display="flex"
      flexDir="row"
      gap="16px"
      overflowX="auto"
      overflowY="hidden"
      h="100%"
      pb="4px"
      sx={{
        "&::-webkit-scrollbar": { height: "5px" },
        "&::-webkit-scrollbar-thumb": {
          background: theme.primary,
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-track": { background: "transparent" },
      }}
    >
      {KANBAN_COLUMNS.map((col) => (
        <KanbanColumn
          key={col.title}
          title={col.title}
          columnFases={col.fases}
          filters={filters}
          selectedFases={debouncedSelectedFases}
          refreshKey={debouncedRefreshKey}
          onClickCard={onClickCard}
          renderDropdown={renderDropdown}
          sharedMode={hasGlobalFilter}
          sharedItems={itemsByColumn.get(col.title)}
          sharedLoading={sharedFeed.loading}
          sharedLoadingMore={sharedFeed.loadingMore}
          sharedHasMore={sharedFeed.hasMore}
          onSharedLoadMore={sharedFeed.handleLoadMore}
        />
      ))}
    </Box>
  );
}

export default React.memo(KanbanMyOportunity);
